// Navigation breadcrumb trail.
//
// A page-chrome–driven stack tracking the user's navigation path inside the
// app. Pages register their own crumb (label + href) via
// `<CurrentPageCrumbProvider>`. When a user follows an in-place link (e.g.
// `<EntityLink>`), the link pushes the current page's crumb onto the trail
// and navigates to the new URL. The destination page reads the trail and
// renders it as the leading items of its breadcrumb.
//
// State lives in module scope (subscribed via `useSyncExternalStore`) so it
// survives MemoryRouter / route component swaps without going through
// React Router's `location.state`. It does NOT persist across reloads —
// breadcrumb chains are an ephemeral navigation affordance.

import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { useLocation } from "react-router";
import { useNavigate as useTabNavigate } from "./navigation";

export interface Crumb {
  label: string;
  href: string;
}

// ─── Store ────────────────────────────────────────────────────────────────

let trail: Crumb[] = [];
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

function getSnapshot(): Crumb[] {
  return trail;
}

function setTrail(next: Crumb[]) {
  if (next === trail) return;
  trail = next;
  emit();
}

export function useNavTrail(): Crumb[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Truncate the trail to the first `index` entries (inclusive). Used by
 * breadcrumb item clicks: clicking item N keeps items [0..N-1] (those before
 * it) and drops everything after. Item N becomes the new destination.
 */
export function truncateTrailTo(index: number) {
  if (index < 0) {
    setTrail([]);
    return;
  }
  if (index >= trail.length) return;
  setTrail(trail.slice(0, index));
}

/** Mark the next location change as trail-driven so the location guard
 *  doesn't clear the trail. Used by breadcrumb-item click handlers that
 *  truncate the trail and navigate back. */
export function markNextNavTrailed() {
  pendingTrailedNav = true;
}

export function clearTrail() {
  if (trail.length === 0) return;
  setTrail([]);
}

// ─── Current page identity (global store) ─────────────────────────────────
//
// The active page registers its own `Crumb` (label + href) via
// `useRegisterPageCrumb`. The persistent shell breadcrumb bar reads this
// alongside the trail and renders a single, never-unmounting bar — so
// navigation between pages doesn't flash the breadcrumb out and back in
// while the destination is in its loading state.

let pageCrumb: Crumb | null = null;
const pageCrumbListeners = new Set<() => void>();

function emitPageCrumb() {
  for (const l of pageCrumbListeners) l();
}

function subscribePageCrumb(l: () => void): () => void {
  pageCrumbListeners.add(l);
  return () => {
    pageCrumbListeners.delete(l);
  };
}

function getPageCrumbSnapshot(): Crumb | null {
  return pageCrumb;
}

export function usePageCrumb(): Crumb | null {
  return useSyncExternalStore(
    subscribePageCrumb,
    getPageCrumbSnapshot,
    getPageCrumbSnapshot,
  );
}

/** Page chromes call this on mount/when their label or href changes. The
 *  registration is cleared on unmount. */
export function useRegisterPageCrumb(crumb: Crumb | null) {
  // Destructured to primitives so the effect keys off the crumb's *value*,
  // not the (usually inline) object's identity. A `Crumb` is exactly
  // `{ label, href }`, so the object rebuilt inside the effect is equivalent
  // to the one passed in.
  const hasCrumb = crumb != null;
  const label = crumb?.label;
  const href = crumb?.href;
  useEffect(() => {
    pageCrumb = hasCrumb ? { label: label as string, href: href as string } : null;
    emitPageCrumb();
    return () => {
      pageCrumb = null;
      emitPageCrumb();
    };
  }, [hasCrumb, label, href]);
}

// ─── Page actions ──────────────────────────────────────────────────────────
//
// The action ladder registers here so the persistent breadcrumb bar can print
// it on the SAME line as the crumbs. Two stacked bars — crumbs on one row,
// buttons on the next — cost a whole row of vertical space to say nothing.
// Same store shape as `pageCrumb`: the bar never unmounts, so actions survive
// the destination page's loading state without flashing.

let pageActions: ReactNode = null;
const pageActionListeners = new Set<() => void>();

function emitPageActions() {
  for (const l of pageActionListeners) l();
}

function subscribePageActions(l: () => void): () => void {
  pageActionListeners.add(l);
  return () => {
    pageActionListeners.delete(l);
  };
}

function getPageActionsSnapshot(): ReactNode {
  return pageActions;
}

export function usePageActions(): ReactNode {
  return useSyncExternalStore(
    subscribePageActions,
    getPageActionsSnapshot,
    getPageActionsSnapshot,
  );
}

/** Page chromes call this on mount; the registration clears on unmount. */
export function useRegisterPageActions(node: ReactNode) {
  useEffect(() => {
    pageActions = node;
    emitPageActions();
    return () => {
      pageActions = null;
      emitPageActions();
    };
  }, [node]);
}

// ─── Legacy context API ────────────────────────────────────────────────────
// `useNavigateWithCrumb` reads the current page identity to push onto the
// trail. Kept as a context for callers that already wrap; new code should
// use `useRegisterPageCrumb` (which also feeds the persistent bar).

export const CurrentPageCrumbContext = createContext<Crumb | null>(null);

export function useCurrentPageCrumb(): Crumb | null {
  // Prefer the global store (set by `useRegisterPageCrumb`); fall back to
  // the legacy context for callers that haven't migrated.
  const fromStore = usePageCrumb();
  const fromContext = useContext(CurrentPageCrumbContext);
  return fromStore ?? fromContext;
}

// ─── Location guard — clears the trail on external navigations ───────────
//
// `useNavigateWithCrumb` sets `pendingTrailedNav` right before navigating so
// the location-change effect in the next render can tell "trail push" apart
// from "external navigation" (sider menu, browser back/forward, Refine
// resource link). External navigations clear the trail; trailed navigations
// consume the flag and leave the trail intact.

let pendingTrailedNav = false;

export function useTrailLocationGuard() {
  const location = useLocation();
  const key = location.pathname + location.search;
  // Fire on EVERY location change including the mount — chrome mounts after
  // a sider-menu click are exactly the case we need to clear the trail for.
  // Trail-driven navigations from `useNavigateWithCrumb` (and
  // `markNextNavTrailed`) set `pendingTrailedNav` immediately before
  // navigating, so the new chrome's mount effect consumes the flag and
  // preserves the trail.
  useEffect(() => {
    if (pendingTrailedNav) {
      pendingTrailedNav = false;
    } else {
      clearTrail();
    }
     
  }, [key]);
}

// ─── Navigation seam ──────────────────────────────────────────────────────

/**
 * In-place navigation that pushes the current page's crumb onto the trail
 * before navigating. Use this from any in-page link (EntityLink, row click,
 * etc.) where the user expects a "back via breadcrumb" affordance.
 *
 * If no current crumb is registered (the chrome didn't publish one), this
 * still navigates but doesn't grow the trail.
 */
export function useNavigateWithCrumb() {
  const navigate = useTabNavigate();
  const current = useCurrentPageCrumb();
  // Capture by ref so the returned callback stays stable across renders
  // (EntityLink memoises around it).
  const currentRef = useRef(current);
  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  return useCallback(
    (url: string) => {
      const c = currentRef.current;
      if (c) {
        // Dedupe: don't push the same crumb twice in a row (rapid double
        // clicks, or navigating to a URL that's already at the top).
        const last = trail[trail.length - 1];
        if (!last || last.href !== c.href) {
          setTrail([...trail, c]);
        }
      }
      pendingTrailedNav = true;
      navigate(url);
    },
    [navigate],
  );
}

/**
 * Merge the live trail with a page's own leading items (e.g. "Strona
 * główna"). The result is appended-with by the current page label by the
 * caller via the same breadcrumb prop.
 */
export function useBreadcrumbWithTrail(
  leading: Crumb[] | undefined,
  current: Crumb | null,
): Array<Crumb & { _trailIndex?: number }> {
  const live = useNavTrail();
  return useMemo(() => {
    const out: Array<Crumb & { _trailIndex?: number }> = [];
    if (leading) out.push(...leading);
    live.forEach((c, i) => out.push({ ...c, _trailIndex: i }));
    if (current) out.push({ label: current.label, href: current.href });
    return out;
  }, [leading, live, current]);
}
